
import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getSafeKey } from '@/src/utils/formatters';
import { BusinessIdea } from '@/src/types';
import { useReporting } from '@/src/hooks/useReporting';
import { useAuth } from '@/src/contexts/AuthContext';

interface SavedIntelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  savedVentures: BusinessIdea[];
  isPurgeConfirmOpen: boolean;
  setIsPurgeConfirmOpen: (open: boolean) => void;
  handleClearIntelligence: () => void;
  confirmPurge: () => void;
  savedHubFilter: 'all' | 'launchpad' | 'discovery' | 'optimize';
  setSavedHubFilter: (filter: 'all' | 'launchpad' | 'discovery' | 'optimize') => void;
  setSelectedIdea: (idea: BusinessIdea) => void;
  handleToggleSave: (idea: BusinessIdea) => void;
  setIsCompareViewOpen: (open: boolean) => void;
  setIsOptimizeUpgradeOpen: (open: boolean) => void;
  subscriptionTier: string;
  onUpgradePrompt: () => void;
}

const SavedIntelDrawer: React.FC<SavedIntelDrawerProps> = ({
  isOpen,
  onClose,
  savedVentures,
  isPurgeConfirmOpen,
  setIsPurgeConfirmOpen,
  handleClearIntelligence,
  confirmPurge,
  savedHubFilter,
  setSavedHubFilter,
  setSelectedIdea,
  handleToggleSave,
  setIsCompareViewOpen,
  setIsOptimizeUpgradeOpen,
  subscriptionTier,
  onUpgradePrompt
}) => {
  const { isLoggedIn, onSignUpPrompt } = useAuth();
  const { exportBusinessSnapshot } = useReporting();
  const isPremium = subscriptionTier === 'premium' || subscriptionTier === 'premium_pro';
  const filteredVentures = useMemo(() => {
    return savedVentures.filter(i => savedHubFilter === 'all' || i.hubOrigin === savedHubFilter);
  }, [savedVentures, savedHubFilter]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="saved-intel-drawer-wrapper" className="fixed inset-0 z-[300] flex justify-end">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/75"
          />
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative bg-[#3a3430] w-full max-w-md h-full border-l border-white/[0.06] flex flex-col text-white z-10"
            onClick={e => e.stopPropagation()}
          >
            <header className="px-6 pt-10 pb-6 flex flex-col border-b border-white/[0.06] bg-[#322D29]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-bold uppercase text-white font-heading tracking-tight">Saved Opportunities</h2>
              {savedVentures.length > 0 && (
                <div className="flex items-center gap-4 mt-1">
                  {isPurgeConfirmOpen ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[8px] font-bold uppercase tracking-widest text-red-400 font-heading">Confirm Purge?</span>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmPurge(); }} className="text-[8px] font-bold uppercase tracking-widest text-red-400 bg-red-950/40 border border-red-500/30 px-2 py-1 rounded hover:bg-red-950/60 transition-colors cursor-pointer">Yes</button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsPurgeConfirmOpen(false); }} className="text-[8px] font-bold uppercase tracking-widest text-white/40 hover:text-white px-2 py-1 transition-colors cursor-pointer">No</button>
                    </div>
                  ) : (
                    <button 
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClearIntelligence(); }}
                      className="text-[8px] font-bold uppercase tracking-[0.2em] text-red-400 hover:text-red-300 transition-colors flex items-center gap-1.5 font-heading cursor-pointer"
                    >
                      <i className="fas fa-trash-can"></i>
                      Purge Archive
                    </button>
                  )}
                </div>
              )}
            </div>
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }} className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
              <i className="fas fa-times text-base"></i>
            </button>
          </div>

          <div className="flex gap-2">
            {[
              { id: 'all', label: 'All Intel', icon: 'fa-layer-group' },
              { id: 'launchpad', label: 'Launchpad', icon: 'fa-rocket' },
              { id: 'discovery', label: 'Discovery', icon: 'fa-satellite' },
              { id: 'optimize', label: 'Optimize', icon: 'fa-gauge-high' }
            ].map((tab) => {
              if (!tab || !tab.id) return null;
              return (
              <button
                key={`filter-tab-${tab.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSavedHubFilter(tab.id as any); }}
                className={`flex-1 py-2.5 flex flex-col items-center gap-1 border rounded-lg transition-all cursor-pointer font-heading ${
                  savedHubFilter === tab.id 
                    ? 'bg-[#F26522] border-[#F26522] text-white' 
                    : 'bg-[#3a3430] border-white/10 text-white/50 hover:text-white hover:border-white/20'
                }`}
              >
                <i className={`fas ${tab.icon} text-[10px]`}></i>
                <span className="text-[8px] font-bold uppercase tracking-widest">{tab.label}</span>
              </button>
            )})}
          </div>
        </header>
        
        {savedVentures.length >= 3 && savedHubFilter === 'all' && (
          <div className="px-6 py-4 bg-[#F26522] text-white space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-white/20 rounded-md flex items-center justify-center text-xs">
                <i className="fas fa-gauge-high"></i>
              </div>
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] font-heading">Scale Protocol Active</span>
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-bold uppercase tracking-tight leading-none font-heading">Ready to Launch?</h3>
              <p className="text-[10px] font-medium text-white/80 leading-relaxed">
                You have {savedVentures.length} validated ideas. Turn them into a live business dashboard with Optimize.
              </p>
            </div>
            <div className="flex gap-2 pt-1">
              <button 
                onClick={() => setIsOptimizeUpgradeOpen(true)}
                className="flex-1 bg-white text-[#F26522] py-2 text-[9px] font-bold uppercase tracking-widest hover:bg-white/90 rounded-lg transition-all font-heading cursor-pointer"
              >
                Choose Venture
              </button>
              <button 
                onClick={() => window.open('https://envesta.ai/optimize/compare', '_blank')}
                className="flex-1 border border-white/30 text-white py-2 text-[9px] font-bold uppercase tracking-widest hover:bg-white/10 rounded-lg transition-all font-heading cursor-pointer"
              >
                Compare Plans
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-[#3a3430] custom-scrollbar">
          {filteredVentures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-white/30 text-center gap-3">
              <i className="fas fa-archive text-3xl"></i>
              <p className="font-bold uppercase tracking-widest text-[10px] font-heading">
                {savedHubFilter === 'all' ? 'No Saved Opportunities' : `No Saved ${savedHubFilter} Opportunities`}
              </p>
            </div>
          ) : filteredVentures.map((idea, i) => {
            if (!idea || (!idea.id && !idea.title)) return null;
            const stableKey = idea.id || getSafeKey('idea', idea, i);
            return (
            <div key={`idea-${stableKey}-${i}`} className="bg-[#322D29] p-5 border border-white/[0.06] rounded-xl cursor-pointer hover:border-white/20 transition-all group relative overflow-hidden" onClick={() => { setSelectedIdea(idea); onClose(); }}>
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[8px] font-bold uppercase tracking-widest font-heading px-2 py-0.5 rounded ${
                    idea.hubOrigin === 'discovery' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25' : 
                    idea.hubOrigin === 'optimize' ? 'bg-[#F26522]/15 text-[#F26522] border border-[#F26522]/25' : 
                    idea.hubOrigin === 'launchpad' ? 'bg-[#F26522]/15 text-[#F26522] border border-[#F26522]/25' : 
                    'bg-white/5 text-white/70 border border-white/10'
                  }`}>
                    {idea.hubOrigin === 'discovery' ? 'Recon' : 
                     idea.hubOrigin === 'optimize' ? 'Live Venture' : 
                     idea.hubOrigin === 'launchpad' ? 'Launchpad' : idea.category}
                  </span>
                  {idea.hubOrigin === 'optimize' && (
                    <span className="flex items-center gap-1 text-[7px] font-bold uppercase tracking-widest text-emerald-400 font-heading">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                      Active
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <button 
                    onClick={() => {
                      if (!isLoggedIn) {
                        onSignUpPrompt();
                        return;
                      }
                      if (!isPremium) {
                        onUpgradePrompt();
                        return;
                      }
                      exportBusinessSnapshot(idea);
                    }}
                    className="w-7 h-7 bg-white/5 hover:bg-white/10 rounded-md flex items-center justify-center text-white/50 hover:text-white transition-all border border-white/10 cursor-pointer"
                    title="Download PDF"
                  >
                    <i className="fas fa-download text-[9px]"></i>
                  </button>
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggleSave(idea); }} className="w-7 h-7 bg-white/5 hover:bg-red-500/20 rounded-md flex items-center justify-center text-red-400/60 hover:text-red-400 transition-all border border-white/10 cursor-pointer">
                    <i className="fas fa-trash-can text-[9px]"></i>
                  </button>
                </div>
              </div>
              <h3 className="text-sm font-bold text-white font-heading">{idea.title}</h3>
              {idea.hubOrigin === 'optimize' && (
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#F26522]" style={{ width: '84%' }}></div>
                  </div>
                  <span className="text-[8px] font-bold text-white/40">84% Health</span>
                </div>
              )}
            </div>
          )})}
        </div>
        {savedVentures.length > 0 && (
          <footer className="p-6 border-t border-white/[0.06] bg-[#322D29]">
            {savedHubFilter === 'all' ? (
              <div className="text-center space-y-3">
                <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Select a Hub to Enter War Room</p>
                <div className="flex gap-2">
                  <button 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSavedHubFilter('launchpad'); }}
                    className="flex-1 bg-[#3a3430] border border-white/10 text-white py-3 font-bold uppercase text-[9px] tracking-widest hover:bg-white/5 rounded-lg transition-all font-heading cursor-pointer"
                  >
                    Launchpad
                  </button>
                  <button 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSavedHubFilter('discovery'); }}
                    className="flex-1 bg-[#3a3430] border border-white/10 text-white py-3 font-bold uppercase text-[9px] tracking-widest hover:bg-white/5 rounded-lg transition-all font-heading cursor-pointer"
                  >
                    Discovery
                  </button>
                  <button 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSavedHubFilter('optimize'); }}
                    className="flex-1 bg-[#3a3430] border border-white/10 text-white py-3 font-bold uppercase text-[9px] tracking-widest hover:bg-white/5 rounded-lg transition-all font-heading cursor-pointer"
                  >
                    Optimize
                  </button>
                </div>
              </div>
            ) : (
              <button 
                onClick={(e) => { 
                  e.preventDefault(); 
                  e.stopPropagation(); 
                  if (!isPremium) {
                    onUpgradePrompt();
                    return;
                  }
                  onClose(); 
                  setIsCompareViewOpen(true); 
                }} 
                className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3.5 font-bold uppercase text-[10px] tracking-[0.2em] rounded-lg transition-all flex items-center justify-center gap-2 font-heading cursor-pointer"
              >
                {!isPremium && <i className="fas fa-lock text-[10px]"></i>}
                Enter {savedHubFilter === 'discovery' ? 'Recon' : savedHubFilter === 'optimize' ? 'Optimize' : 'War'} Room
              </button>
            )}
          </footer>
        )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SavedIntelDrawer;
